import moment from 'moment'
import oikosApi from '../services/oikos'
import { getRegs } from './fc'
import { uploadFileToDocumento } from './oikosUploadDocumento'
import { TP_DOC_REC_LIBERACAO_FATURAMENTO } from '../constants/oikosReceitas'

export const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Valor numérico → máscara tipo 1.234,56 (sempre 2 decimais). */
export const formatBrlFromNumber = (n) => {
  if (n == null || n === '') return ''
  const v = Number(n)
  if (!Number.isFinite(v)) return ''
  const cents = Math.round(v * 100)
  const s = String(Math.abs(cents)).padStart(3, '0')
  const intRaw = s.slice(0, -2) || '0'
  const dec = s.slice(-2)
  const intFmt = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  const sign = v < 0 ? '-' : ''
  return `${sign}${intFmt},${dec}`
}

/** Somente dígitos digitados → máscara 1.234,56 */
export const digitsToBrlMask = (digitsRaw) => {
  const digits = String(digitsRaw || '').replace(/\D/g, '')
  if (!digits) return ''
  const n = Number(digits) / 100
  return formatBrlFromNumber(n)
}

/** Máscara 1.234,56 → número */
export const parseBrlMaskedToNumber = (masked) => {
  const digits = String(masked || '').replace(/\D/g, '')
  if (!digits) return NaN
  return Number(digits) / 100
}

export const filterLibDocs = (docs) => {
  const list = Array.isArray(docs) ? docs : []
  return list
    .filter((d) => Number(d?.tpDocumento_id) === Number(TP_DOC_REC_LIBERACAO_FATURAMENTO))
    .sort((a, b) => Number(b?.arquivo_id || 0) - Number(a?.arquivo_id || 0))
}

export const isRecAutorizado = (rec) => Number(rec?.rec_aut) === 1

const PROD_STATUS_TP_FATURAMENTO_AUTORIZADO = 8
let prodStatusTp8Ensured = false

/** Garante cadastro do tipo de status 8 (Faturamento Autorizado) em `prodStatusTp`. */
export async function ensureProdStatusTpFaturamentoAutorizado() {
  if (prodStatusTp8Ensured) return
  const rows = await getRegs('prodStatusTp', { prodStatusTp_id: PROD_STATUS_TP_FATURAMENTO_AUTORIZADO })
  if (Array.isArray(rows) && rows.length > 0) {
    prodStatusTp8Ensured = true
    return
  }
  try {
    await oikosApi.post('/', {
      action: 'insert',
      table: 'prodStatusTp',
      data: {
        prodStatusTp_id: PROD_STATUS_TP_FATURAMENTO_AUTORIZADO,
        prodStatusTp_nome: 'Faturamento Autorizado',
        prodStatusTp_ativo: 1,
      },
    })
    prodStatusTp8Ensured = true
  } catch (e) {
    console.log('ensureProdStatusTpFaturamentoAutorizado', e)
    throw new Error(
      'Tipo de status 8 (Faturamento Autorizado) não está cadastrado em prodStatusTp. Solicite inclusão ao administrador.'
    )
  }
}

/** Insere status 8 em prodStatusAlt com validação de resposta da API. */
export async function insertProdStatusFaturamentoAutorizado({
  prod_id,
  pf_id,
  prodStatusAlt_obs = '',
  userOikosId,
  prodStatusAlt_data,
}) {
  await ensureProdStatusTpFaturamentoAutorizado()

  const res = await oikosApi.post('/', {
    action: 'insert',
    table: 'prodStatusAlt',
    data: {
      prod_id,
      prodStatusTp_id: PROD_STATUS_TP_FATURAMENTO_AUTORIZADO,
      pf_id,
      prodStatusAlt_obs: prodStatusAlt_obs || '',
      prodStatusAlt_data: prodStatusAlt_data || moment().format('YYYY-MM-DD'),
      prodStatusAlt_usuarioCria: userOikosId,
      prodStatusAlt_usuarioMod: userOikosId,
    },
  })

  const row = res?.data?.[0]
  const insertedId = row?.prodStatusAlt_id
  if (!insertedId) {
    throw new Error(`Falha ao registrar status de faturamento para o produto #${prod_id}.`)
  }
  return insertedId
}

/**
 * Valida e persiste liberação de faturamento na tabela `rec` + uploads PDF tipo 41.
 * @throws {Error} mensagens para notifyError no caller
 */
export async function submitLiberacaoRec({ modalRecRow, libForm, recLibDocs }) {
  if (!modalRecRow?.rec_id) throw new Error('Receita inválida.')
  if (isRecAutorizado(modalRecRow)) return

  const bru = num(modalRecRow.rec_vlrBru)
  const lib = parseBrlMaskedToNumber(libForm.vlrLib)
  const existingLib = filterLibDocs(recLibDocs)
  const hasExisting = existingLib.length > 0
  const newFiles = libForm.pdfFiles || []
  const needJust = Math.abs(bru - lib) > 0.01

  if (!libForm.dtLib) throw new Error('Informe a data da liberação.')
  if (moment(libForm.dtLib).isAfter(moment(), 'day')) {
    throw new Error('A data da liberação não pode estar no futuro.')
  }
  if (!Number.isFinite(lib)) throw new Error('Informe o valor liberado.')
  if (needJust && !(libForm.just && String(libForm.just).trim())) {
    throw new Error('Informe a justificativa para a diferença em relação ao valor bruto previsto.')
  }
  if (!hasExisting && newFiles.length === 0) {
    throw new Error('Inclua pelo menos um PDF de liberação de faturamento.')
  }

  for (let i = 0; i < newFiles.length; i += 1) {
    const file = newFiles[i]
    await uploadFileToDocumento({
      tpDocumentoId: TP_DOC_REC_LIBERACAO_FATURAMENTO,
      tabRef: 'rec',
      tabId: modalRecRow.rec_id,
      file,
      documentoDesc: `Liberação faturamento (rec_id ${modalRecRow.rec_id})${newFiles.length > 1 ? ` [${i + 1}/${newFiles.length}]` : ''}`,
    })
  }

  await oikosApi.post('/', {
    action: 'update',
    table: 'rec',
    data: {
      rec_id: modalRecRow.rec_id,
      rec_aut: 1,
      rec_dtLibAutNf: libForm.dtLib,
      rec_vlrLibAutNf: lib,
      rec_vlrBru: lib,
      rec_justDifLibAutNf: needJust ? String(libForm.just).trim() : null,
    },
  })
}

/** Inicializa libForm a partir da linha `rec` (mesma lógica que EvePag.openModalLibera). */
export function buildInitialLibForm(rec) {
  const today = moment().format('YYYY-MM-DD')
  const dtLib = rec.rec_dtLibAutNf
    ? moment(rec.rec_dtLibAutNf).format('YYYY-MM-DD')
    : today
  const vlrSrc =
    rec.rec_vlrLibAutNf != null && rec.rec_vlrLibAutNf !== ''
      ? rec.rec_vlrLibAutNf
      : rec.rec_vlrBru
  const vlrLib = formatBrlFromNumber(vlrSrc)
  return {
    dtLib,
    vlrLib,
    just: rec.rec_justDifLibAutNf || '',
    pdfFiles: [],
  }
}
